/**
 * Classick — product deal selector.
 *
 * Works together with snippets/ck-bundle-3pack.liquid.
 * Adds the chosen variants to the cart as their original SKUs;
 * the 30% discount is applied by the "Build Your 3-Pack — 30% off"
 * automatic discount (min. 3 items from the build-your-3-pack collection).
 * On the two CLS Aero Socks pages it also adds a real sock variant and a real
 * SF Aero Wraps 2.0 variant. Shopify's automatic BXGY discount makes the
 * wraps free while inventory keeps tracking both variants separately.
 */
(() => {
  if (customElements.get('ck-bundle-selector')) return;

  const DISCOUNT = 0.125;
  const BUNDLE_PROPERTY = { Bundel: 'Stel je 3-pack samen (30% totaalvoordeel)' };
  const COMBO_PROPERTY = { Combideal: 'CLS Aero Socks + gratis SF Aero Wraps 2.0' };

  class CkBundleSelector extends HTMLElement {
    connectedCallback() {
      // Never activate inside the quick-add modal (CSS hides it there too).
      if (this.closest('.quick-add-modal__content')) return;

      const dataEl = document.querySelector(
        `script[data-ck-bundle-data="${this.dataset.sectionId}"]`
      );
      if (!dataEl) return;

      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      this.slots = [null, null, null]; // { productId, variantId }
      this.activeSlot = 0;
      this.productForm = this.closest('.buy-buttons-block')?.querySelector('product-form-component');
      this.currentPagePrice = this.#pageVariantPrice() ?? this.data.products[0]?.price ?? 0;
      this.money = new Intl.NumberFormat(this.data.locale || 'en', {
        style: 'currency',
        currency: this.data.currency || 'EUR',
      });

      this.dialog = this.querySelector('[data-ck-dialog]');
      this.cta = this.querySelector('[data-ck-cta]');
      this.errorEl = this.querySelector('[data-ck-error]');
      this.comboCta = this.querySelector('[data-ck-combo-cta]');
      this.comboErrorEl = this.querySelector('[data-ck-combo-error]');
      this.selectedWrapVariant = Number(
        this.querySelector('[data-ck-wrap-size].is-selected')?.dataset.variantId
      ) || null;

      this.#bind();
      this.#applyMode(this.#mode());
      this.#renderSummary();
      this.#observeViewed();
      this.#listenVariantUpdates();
    }

    /* ---------- setup ---------- */

    #bind() {
      this.querySelectorAll('[data-ck-mode-input]').forEach((input) => {
        input.addEventListener('change', () => {
          const mode = this.#mode();
          this.#applyMode(mode);
          const eventName = {
            bundle: 'three_pack_selected',
            combo: 'aero_combo_selected',
            single: 'single_pair_selected',
          }[mode];
          this.#track(eventName, mode === 'combo' ? this.#comboPayload() : this.#payload());
        });
      });

      this.querySelectorAll('[data-ck-slot]').forEach((slot) => {
        slot.addEventListener('click', () => {
          this.#selectBundleMode();
          this.#openBuilder(Number(slot.dataset.ckSlot));
        });
      });

      this.cta?.addEventListener('click', () => {
        if (this.#isComplete()) this.#addToCart();
        else this.#openBuilder(this.#firstEmptySlot());
      });

      this.querySelectorAll('[data-ck-wrap-size]').forEach((button) => {
        button.addEventListener('click', () => {
          this.selectedWrapVariant = Number(button.dataset.variantId);
          this.querySelectorAll('[data-ck-wrap-size]').forEach((item) => {
            const selected = item === button;
            item.classList.toggle('is-selected', selected);
            item.setAttribute('aria-pressed', String(selected));
          });
          this.#hideComboError();
          this.#track('aero_combo_wrap_size_selected', this.#comboPayload());
        });
      });

      this.comboCta?.addEventListener('click', () => this.#addComboToCart());

      this.querySelector('[data-ck-close]')?.addEventListener('click', () => this.dialog?.close());
      this.querySelector('[data-ck-back]')?.addEventListener('click', () => this.#showProductStep());
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
    }

    #mode() {
      return (
        this.querySelector('[data-ck-mode-input]:checked')?.value ||
        (this.data.wrapsProduct ? 'combo' : this.data.products.length ? 'bundle' : 'single')
      );
    }

    #selectBundleMode() {
      const input = this.querySelector('[data-ck-mode-input][value="bundle"]');
      if (!input.checked) {
        input.checked = true;
        this.#applyMode('bundle');
      }
    }

    #applyMode(mode) {
      const bundle = mode === 'bundle';
      const combo = mode === 'combo';
      this.querySelectorAll('[data-ck-option]').forEach((option) => {
        option.toggleAttribute('data-ck-selected', option.dataset.ckOption === mode);
      });
      const bundleBody = this.querySelector('[data-ck-body]');
      const comboBody = this.querySelector('[data-ck-combo-body]');
      if (bundleBody) bundleBody.hidden = !bundle;
      if (comboBody) comboBody.hidden = !combo;
      this.productForm?.classList.toggle('ck-bundle-hide-form', mode !== 'single');
    }

    /* ---------- pricing ---------- */

    #pageVariantPrice(variantId) {
      const id =
        variantId ??
        Number(this.productForm?.querySelector('input[name="id"]')?.value) ??
        NaN;
      return this.data.pageVariants.find((v) => v.id === id)?.price ?? this.data.pageVariants[0]?.price;
    }

    #slotPrices() {
      return this.slots.map((slot) => {
        if (!slot) return this.currentPagePrice;
        const product = this.#product(slot.productId);
        return product.variants.find((v) => v.id === slot.variantId)?.price ?? product.price;
      });
    }

    #totals() {
      const prices = this.#slotPrices();
      const original = prices.reduce((sum, p) => sum + Math.round(p / 0.8), 0);
      // Same per-line rounding as Shopify's automatic percentage discount.
      const discounted = prices.reduce((sum, p) => sum + Math.round(p * (1 - DISCOUNT)), 0);
      return { original, discounted, savings: original - discounted };
    }

    #fmt(cents) {
      return this.money.format(cents / 100);
    }

    #renderSummary() {
      const { original, discounted, savings } = this.#totals();
      const originalEl = this.querySelector('[data-ck-orig]');
      const bundlePriceEl = this.querySelector('[data-ck-bundle-price]');
      const saveEl = this.querySelector('[data-ck-save]');
      const perPairEl = this.querySelector('[data-ck-per-pair]');
      if (originalEl) originalEl.textContent = this.#fmt(original);
      if (bundlePriceEl) bundlePriceEl.textContent = this.#fmt(discounted);
      if (saveEl) saveEl.textContent = `Je bespaart ${this.#fmt(savings)}`;
      if (perPairEl) perPairEl.textContent = `${this.#fmt(Math.round(discounted / 3))} per item`;
      if (this.cta) this.cta.textContent = this.#isComplete() ? 'Voeg 3-pack toe' : 'Stel mijn 3-pack samen';
      this.#renderComboPrice();
    }

    #renderComboPrice() {
      if (!this.data.wrapsProduct) return;
      const wrapsPrice = this.data.wrapsProduct.price || 0;
      const compareEl = this.querySelector('[data-ck-combo-compare]');
      const priceEl = this.querySelector('[data-ck-combo-price]');
      if (compareEl) compareEl.textContent = this.#fmt(this.currentPagePrice + wrapsPrice);
      if (priceEl) priceEl.textContent = this.#fmt(this.currentPagePrice);
      if (this.comboCta) {
        this.comboCta.textContent = `Voeg combideal toe — ${this.#fmt(this.currentPagePrice)}`;
      }
    }

    /* ---------- slots / builder ---------- */

    #product(id) {
      return this.data.products.find((p) => p.id === id);
    }

    #titleParts(product) {
      const parts = product.title.split('|').map((s) => s.trim());
      return { name: parts[0] || product.title, colour: parts[1] || '' };
    }

    #isComplete() {
      return this.slots.every(Boolean);
    }

    #firstEmptySlot() {
      const index = this.slots.findIndex((slot) => !slot);
      return index === -1 ? 0 : index;
    }

    #openBuilder(slotIndex) {
      this.activeSlot = slotIndex;
      this.querySelector('[data-ck-dialog-title]').textContent = `Kies item ${slotIndex + 1}`;
      this.#showProductStep();
      this.dialog.showModal();
      this.#track('bundle_builder_opened', { ...this.#payload(), slot: slotIndex + 1 });
    }

    #showProductStep() {
      const step = this.querySelector('[data-ck-step-products]');
      this.querySelector('[data-ck-step-sizes]').hidden = true;
      this.querySelector('[data-ck-back]').hidden = true;
      step.hidden = false;

      if (!step.dataset.rendered) {
        const grid = document.createElement('div');
        grid.className = 'ck-bundle__grid';
        this.data.products.forEach((product) => {
          const { name, colour } = this.#titleParts(product);
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ck-bundle__product';
          button.disabled = !product.available;
          button.innerHTML = `
            ${product.image ? `<img src="${product.image}" alt="" loading="lazy" width="180" height="180">` : ''}
            <span class="ck-bundle__product-name">${name}</span>
            <span class="ck-bundle__product-colour">${colour}${product.available ? '' : ' — Uitverkocht'}</span>
            <span class="ck-bundle__product-price">${this.#fmt(product.price)}</span>
          `;
          button.addEventListener('click', () => this.#showSizeStep(product));
          grid.appendChild(button);
        });
        step.appendChild(grid);
        step.dataset.rendered = 'true';
      }
    }

    #showSizeStep(product) {
      const step = this.querySelector('[data-ck-step-sizes]');
      const { name, colour } = this.#titleParts(product);
      this.querySelector('[data-ck-step-products]').hidden = true;
      this.querySelector('[data-ck-back]').hidden = false;
      step.hidden = false;
      step.innerHTML = `
        <div class="ck-bundle__sizes-product">
          ${product.image ? `<img src="${product.image}" alt="" width="52" height="52">` : ''}
          <div>
            <div class="ck-bundle__sizes-product-name">${name}</div>
            <div class="ck-bundle__product-colour">${colour}</div>
          </div>
        </div>
        <p class="ck-bundle__sizes-label">Kies je maat</p>
        <div class="ck-bundle__sizes"></div>
      `;
      const sizes = step.querySelector('.ck-bundle__sizes');
      product.variants.forEach((variant) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ck-bundle__size';
        button.disabled = !variant.available;
        button.textContent = variant.title;
        button.addEventListener('click', () => this.#fillSlot(product, variant));
        sizes.appendChild(button);
      });
    }

    #fillSlot(product, variant) {
      this.slots[this.activeSlot] = { productId: product.id, variantId: variant.id };
      this.#renderSlot(this.activeSlot);
      this.#renderSummary();
      this.#hideError();

      if (this.#isComplete()) {
        this.dialog.close();
        this.#track('bundle_completed', this.#payload());
      } else {
        // Move straight on to the next empty pair.
        this.activeSlot = this.#firstEmptySlot();
        this.querySelector('[data-ck-dialog-title]').textContent = `Kies item ${this.activeSlot + 1}`;
        this.#showProductStep();
      }
    }

    #renderSlot(index) {
      const slotEl = this.querySelector(`[data-ck-slot="${index}"]`);
      const slot = this.slots[index];
      if (!slot) return;
      const product = this.#product(slot.productId);
      const variant = product.variants.find((v) => v.id === slot.variantId);
      const { name, colour } = this.#titleParts(product);

      slotEl.setAttribute('data-ck-filled', '');
      slotEl.querySelector('[data-ck-slot-thumb]').innerHTML = product.image
        ? `<img src="${product.image}" alt="" width="44" height="44">`
        : '';
      slotEl.querySelector('[data-ck-slot-sub]').textContent = `${name} — ${colour} · ${variant.title}`;
      slotEl.querySelector('[data-ck-slot-action]').textContent = 'Wijzig';
    }

    /* ---------- cart ---------- */

    async #addToCart() {
      this.#hideError();
      this.cta.disabled = true;
      const originalLabel = this.cta.textContent;
      this.cta.textContent = 'Toevoegen…';

      // Merge duplicate variants into a single line with quantity.
      const merged = new Map();
      this.slots.forEach((slot) => {
        merged.set(slot.variantId, (merged.get(slot.variantId) || 0) + 1);
      });
      const items = [...merged.entries()].map(([id, quantity]) => ({
        id,
        quantity,
        properties: BUNDLE_PROPERTY,
      }));

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.description || result.message || 'De 3-pack kon niet worden toegevoegd. Probeer het opnieuw.');
        }

        this.#track('bundle_added_to_cart', this.#payload());

        // Let the theme refresh the cart drawer, cart icon and cart page.
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: result,
              sourceId: `ck-bundle-${this.data.sectionId}`,
              data: { source: 'product-form-component', itemCount: 3, didError: false },
            },
          })
        );

        this.cta.textContent = 'Toegevoegd ✓';
        setTimeout(() => {
          this.cta.textContent = originalLabel;
          this.cta.disabled = false;
        }, 2000);
      } catch (error) {
        this.cta.textContent = originalLabel;
        this.cta.disabled = false;
        this.#showError(error.message);
      }
    }

    async #addComboToCart() {
      this.#hideComboError();
      const sockVariantId = Number(this.productForm?.querySelector('input[name="id"]')?.value);
      const sockVariant = this.data.pageVariants.find((variant) => variant.id === sockVariantId);
      const wrapVariant = this.data.wrapsProduct?.variants.find(
        (variant) => variant.id === this.selectedWrapVariant
      );

      if (!sockVariant?.available) {
        this.#showComboError('Kies eerst een beschikbare maat voor de CLS Aero Socks.');
        return;
      }
      if (!wrapVariant?.available) {
        this.#showComboError('Kies eerst een beschikbare maat voor de SF Aero Wraps 2.0.');
        return;
      }

      this.comboCta.disabled = true;
      const originalLabel = this.comboCta.textContent;
      this.comboCta.textContent = 'Toevoegen…';

      const items = [
        { id: sockVariantId, quantity: 1, properties: COMBO_PROPERTY },
        { id: this.selectedWrapVariant, quantity: 1, properties: COMBO_PROPERTY },
      ];

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(
            result.description || result.message || 'De combideal kon niet worden toegevoegd. Probeer het opnieuw.'
          );
        }

        const payload = this.#comboPayload();
        this.#track('aero_combo_added_to_cart', payload);
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: result,
              sourceId: `ck-aero-combo-${this.data.sectionId}`,
              data: { source: 'product-form-component', itemCount: 2, didError: false },
            },
          })
        );

        this.comboCta.textContent = 'Combideal toegevoegd ✓';
        setTimeout(() => {
          this.comboCta.textContent = originalLabel;
          this.comboCta.disabled = false;
        }, 2000);
      } catch (error) {
        this.comboCta.textContent = originalLabel;
        this.comboCta.disabled = false;
        this.#showComboError(error.message);
      }
    }

    #showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    #hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    #showComboError(message) {
      if (!this.comboErrorEl) return;
      this.comboErrorEl.textContent = message;
      this.comboErrorEl.hidden = false;
    }

    #hideComboError() {
      if (this.comboErrorEl) this.comboErrorEl.hidden = true;
    }

    /* ---------- page variant sync ---------- */

    #listenVariantUpdates() {
      document.addEventListener('variant:update', (event) => {
        const variantId = event.detail?.resource?.id;
        if (!variantId) return;
        const price = this.#pageVariantPrice(Number(variantId));
        if (price == null) return;
        this.currentPagePrice = price;
        this.querySelector('[data-ck-single-price]').textContent = this.#fmt(price);
        this.#renderSummary();
      });
    }

    /* ---------- tracking ---------- */

    #payload() {
      const { original, discounted, savings } = this.#totals();
      return {
        bundle: 'build-your-3-pack',
        product_ids: this.slots.filter(Boolean).map((slot) => slot.productId),
        variant_ids: this.slots.filter(Boolean).map((slot) => slot.variantId),
        original_value: Number((original / 100).toFixed(2)),
        discounted_value: Number((discounted / 100).toFixed(2)),
        savings: Number((savings / 100).toFixed(2)),
        currency: this.data.currency,
      };
    }

    #comboPayload() {
      const sockVariantId = Number(this.productForm?.querySelector('input[name="id"]')?.value) || null;
      const wrapsPrice = this.data.wrapsProduct?.price || 0;
      return {
        bundle: 'cls-aero-socks-free-sf-aero-wraps-2-0',
        product_ids: [this.data.pageProductId, this.data.wrapsProduct?.id].filter(Boolean),
        variant_ids: [sockVariantId, this.selectedWrapVariant].filter(Boolean),
        original_value: Number(((this.currentPagePrice + wrapsPrice) / 100).toFixed(2)),
        discounted_value: Number((this.currentPagePrice / 100).toFixed(2)),
        savings: Number((wrapsPrice / 100).toFixed(2)),
        currency: this.data.currency,
      };
    }

    #track(eventName, payload) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...payload });
      document.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: payload }));
    }

    #observeViewed() {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            const mode = this.#mode();
            this.#track(
              mode === 'combo' ? 'aero_combo_offer_viewed' : 'bundle_offer_viewed',
              mode === 'combo' ? this.#comboPayload() : this.#payload()
            );
            observer.disconnect();
          }
        },
        { threshold: 0.4 }
      );
      observer.observe(this);
    }
  }

  customElements.define('ck-bundle-selector', CkBundleSelector);
})();
